import { Link } from 'react-router-dom';
import { Fragment } from 'react';
import { Paperclip } from 'lucide-react';
import { RelativeTime } from '@/components/RelativeTime';
import {
  highlightSegments,
  snippetSegments,
  type SearchMatch,
  type Segment,
} from '@/lib/search-query';
import { cn } from '@/lib/utils';
import type { TeamMember } from '@/lib/queries';

// The "why did this ticket match?" line under a search result.
//
// Safety: the snippet comes from the database and contains customer email
// text. It is rendered ONLY as React text nodes + <mark> (never
// dangerouslySetInnerHTML), so markup inside an email can't execute.

function renderSegments(segments: Segment[]) {
  return segments.map((s, i) =>
    s.hit ? (
      <mark
        key={i}
        className="rounded-sm bg-tint-warning px-0.5 font-medium text-tint-warning-fg"
      >
        {s.text}
      </mark>
    ) : (
      <Fragment key={i}>{s.text}</Fragment>
    ),
  );
}

/** `<@U123ABC>` mentions in notes read better as a name. */
function resolveMentions(text: string, members: Map<string, TeamMember> | undefined): string {
  if (!members) return text;
  return text.replace(/<@([A-Z0-9]+)>/g, (_m, id: string) => `@${members.get(id)?.name ?? 'someone'}`);
}

interface LabelParts {
  label: string;
  at?: string;
}

function labelFor(
  match: SearchMatch,
  members: Map<string, TeamMember> | undefined,
): LabelParts | null {
  switch (match.source) {
    case 'subject':
      return null; // the row's subject is highlighted instead
    case 'id':
      return { label: `Ticket ID ${match.text ?? ''}`.trim() };
    case 'sender':
      return { label: 'Sender' };
    case 'contact':
      return { label: 'Contact' };
    case 'summary':
      return { label: 'Summary' };
    case 'recipient':
      return { label: 'To / CC / attachment', at: match.message_at };
    case 'note': {
      const who = match.author_slack_id ? members?.get(match.author_slack_id)?.name : null;
      return { label: who ? `Internal note by ${who}` : 'Internal note', at: match.message_at };
    }
    case 'message':
      return {
        label: match.direction === 'outbound' ? 'Team reply' : 'Message from client',
        at: match.message_at,
      };
  }
}

export function SearchSnippet({
  match,
  terms,
  to,
  teamMembersBySlackId,
  className,
}: {
  match: SearchMatch;
  /** Words to highlight when the server sent plain text (no markers). */
  terms: string[];
  /** Where clicking the line goes (the ticket, with ?find= and ?m=). */
  to: string;
  teamMembersBySlackId?: Map<string, TeamMember>;
  className?: string;
}) {
  const parts = labelFor(match, teamMembersBySlackId);
  if (!parts) return null;

  // Server snippets carry hit markers; plain text is highlighted client-side.
  const raw = match.snippet ?? match.text ?? '';
  if (!raw) return null;
  const text = resolveMentions(raw, teamMembersBySlackId);
  const hasMarkers = /[\u0002\u0003]/.test(text);
  const segments = hasMarkers ? snippetSegments(text) : highlightSegments(text, terms);
  const more = (match.message_hits ?? 0) - 1;

  return (
    <Link
      to={to}
      className={cn(
        'flex min-w-0 items-baseline gap-1.5 rounded-md px-3 pb-2 pt-0.5 text-xs text-muted-foreground',
        'hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className,
      )}
    >
      <span className="inline-flex shrink-0 items-center gap-1 text-3xs font-semibold uppercase tracking-wide text-muted-foreground/80">
        {match.source === 'recipient' ? <Paperclip className="h-2.5 w-2.5" aria-hidden /> : null}
        {parts.label}
        {parts.at ? (
          <>
            <span aria-hidden>·</span>
            <RelativeTime value={parts.at} className="normal-case tracking-normal" />
          </>
        ) : null}
      </span>
      <span className="min-w-0 flex-1 truncate" title={text.replace(/[\u0002\u0003]/g, '')}>
        {renderSegments(segments)}
      </span>
      {more > 0 ? (
        <span className="shrink-0 text-3xs text-muted-foreground/80">
          +{more} more message{more === 1 ? '' : 's'}
        </span>
      ) : null}
    </Link>
  );
}
